import {
  METRIC_LABELS,
  METRIC_ORDER,
  type AppliedScenarioMetricKey,
} from "@/types/applied-scenario.types";
import type { TrackRecordScope } from "@/types/track-record.types";
import { BiasValue } from "./BiasValue";
import { magnitude } from "./format";

interface TrackRecordMetricRowsProps {
  scope: TrackRecordScope;
}

// Spec 079 (FR-006/FR-007) — erro médio e viés, POR MÉTRICA.
//
// ⚠️ Nunca há um "erro médio geral". Pontos percentuais de OEE e unidades de produção boa não se
// somam, e a média entre eles seria um número com aparência de indicador e sem significado nenhum.
// A tabela existe justamente para que a pergunta "qual é o erro do gêmeo?" só tenha resposta por
// métrica.
//
// A ORDEM vem de `METRIC_ORDER`, o mesmo do contrato e da apuração — nunca a ordem em que o `jsonb`
// chegou. Uma cópia local divergiria no dia em que alguém acrescentasse a sexta métrica.

/**
 * Erro médio: valor ABSOLUTO, sempre positivo. Nulo é ausência de par, jamais erro zero.
 *
 * A unidade vem do MESMO helper que o viés usa (`format.ts`) — enquanto eram duas cópias da regra
 * `PERCENT_METRICS.has(...)`, uma sexta métrica faria erro e viés discordarem na mesma linha.
 */
function fmtError(value: number | null, metric: AppliedScenarioMetricKey): string {
  if (value === null) return "—";
  return magnitude(value, metric);
}

export function TrackRecordMetricRows({ scope }: TrackRecordMetricRowsProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-sm">
        <caption className="sr-only">
          Erro médio e viés do gêmeo, por métrica, desde o marco zero
        </caption>
        <thead>
          <tr className="border-b border-border-subtle text-left text-[11px] uppercase tracking-wide text-text-tertiary">
            <th scope="col" className="py-1.5 pr-3 font-medium">
              Métrica
            </th>
            <th scope="col" className="py-1.5 pr-3 text-right font-medium">
              Erro médio
            </th>
            <th scope="col" className="py-1.5 text-right font-medium">
              Viés
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {METRIC_ORDER.map((metric) => (
            <tr key={metric}>
              <th scope="row" className="py-2 pr-3 text-left text-[13px] font-normal">
                {METRIC_LABELS[metric]}
              </th>
              <td className="py-2 pr-3 text-right font-mono tabular-nums">
                {fmtError(scope.errorByMetric[metric] ?? null, metric)}
              </td>
              <td className="py-2 text-right">
                <BiasValue metric={metric} value={scope.biasByMetric[metric] ?? null} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
